import { Decimal } from 'decimal.js'

import { CalculadoraDePercentual } from '@/utils/calculadora-de-percentual'
import { formatToBRL } from '@/utils/currency.utils'

import type {
  IProcessadorValoresDecimal,
  TGrupoIntermediarias,
  TGrupoMensais,
  TGrupoMensaisPosConclusao,
  TGrupoParcela,
} from './processador-valores-decimal'
import { ProcessadorValoresDecimal } from './processador-valores-decimal'

export type TGrupoParcelasValoresTotais = {
  entradas?: TGrupoParcela[]
  mensais?: TGrupoMensais[]
  intermediarias?: TGrupoIntermediarias[]
  posConclusao?: TGrupoMensaisPosConclusao
  saldo?: TGrupoParcela | null
  intermediariasPosConclusao?: TGrupoIntermediarias[]
}

export type TValorLegendaGraficoTipo = 'poupanca' | 'saldo' | 'beneficio' | 'posConclusao'

export type TValorLegendaGrafico = {
  tipo: TValorLegendaGraficoTipo
  value: number
  percentual: string | null
}

export class CalculadoraDeValoresTotaisBase {
  constructor(
    protected readonly grupos: TGrupoParcelasValoresTotais,
    protected readonly valorTabela = 0,
    protected readonly processador: IProcessadorValoresDecimal = new ProcessadorValoresDecimal(),
  ) {}

  protected percentualFormatado(valor: number) {
    return this.calcularPercentual.calcularPercentualFormatado(valor)
  }

  protected get calcularPercentual() {
    return new CalculadoraDePercentual([this.valorSaldo, this.valorPoupanca])
  }

  percentualPoupancaFormatado() {
    return this.percentualFormatado(this.valorPoupanca)
  }

  percentualSaldoFormatado() {
    return this.percentualFormatado(this.valorSaldo)
  }

  get valoresLegendaGrafico(): TValorLegendaGrafico[] {
    return [
      {
        tipo: 'poupanca' as const,
        value: this.valorPoupanca,
        percentual: this.percentualPoupancaFormatado(),
      },
      {
        tipo: 'saldo' as const,
        value: this.valorSaldo,
        percentual: this.percentualSaldoFormatado(),
      },
      {
        tipo: 'beneficio' as const,
        value: this.valorBeneficio,
        percentual: null,
      },
    ]
  }

  get valoresChart() {
    const calculadoraPercentual = new CalculadoraDePercentual([this.valorPoupanca, this.valorSaldo])
    return [
      {
        tipo: 'poupanca' as const,
        valor: formatToBRL(this.valorPoupanca),
        percentual: calculadoraPercentual.calcularPercentual(this.valorPoupanca),
      },
      {
        tipo: 'saldo' as const,
        valor: formatToBRL(this.valorSaldo),
        percentual: calculadoraPercentual.calcularPercentual(this.valorSaldo),
      },
    ]
  }

  get totalDeEntradas() {
    return this.processador.calcularTotalEntradas(this.grupos?.entradas)
  }

  get totalDeMensais() {
    return this.processador.calcularTotalMensais(this.grupos?.mensais)
  }

  get totalDeIntermediarias() {
    return this.processador.calcularTotalIntermediarias(this.grupos?.intermediarias)
  }

  get valorPoupanca() {
    return new Decimal(this.totalDeEntradas).plus(this.totalDeMensais).plus(this.totalDeIntermediarias).toNumber()
  }

  get valorPosConclusao() {
    return this.processador.calcularValorPosConclusao(this.grupos?.posConclusao)
  }

  get valorSaldo() {
    return new Decimal(this.grupos?.saldo?.valor ?? 0).plus(this.valorPosConclusao).toNumber()
  }

  get valorBeneficio() {
    return new Decimal(this.valorTabela).minus(this.valorTotalSimulacao).toNumber()
  }

  get valorTotalSimulacao() {
    return new Decimal(this.valorPoupanca).plus(this.valorSaldo).toNumber()
  }

  get isPagamentoAVista() {
    return this.totalDeEntradas === this.valorTotalSimulacao
  }
}
